import { useState, useEffect } from "react";
import { useStudyPlan } from "../../contexts/study.context";
import { isRemodelStudyPlanType } from "../../../study.plans/study.plan/utils/helpers";
import { stageRemodelActivationProfiles } from "../../../study.plans/study.plan/countries.and.site.groups/country.panel/site.group.activation.profiles/activationProfileSiteCount";

/**
 * Custom hook to handle the warning dialog functionality for sites to activate.
 * Remodel plans always confirm, even though activation-profile override stays on.
 * An existing final Projected using PP card is staged into the save payload.
 * If that card is missing and the site count increased, a new final card is staged too.
 * The panel shows either change only after the save refresh.
 * @param {Object} sites_to_activate - The sites to activate parameter object
 * @param {string} boundProperty - Either "upperBound" or "lowerBound"
 * @param {Function} handleUpdateSiteGroupParameters - Function to update site group parameters
 * @param {Object} [options]
 * @param {string|number} [options.countryId]
 * @param {Array} [options.activationProfiles]
 * @returns {Object} - Object containing all the necessary state and handlers
 */
export const useActivationProfilesWarning = (
  sites_to_activate,
  boundProperty,
  handleUpdateSiteGroupParameters,
  options = {},
) => {
  const { countryId, activationProfiles } = options;
  const {
    studyPlan,
    isEditingAPTableEnabled,
    savePlan,
    stageCountryActivationProfiles,
    setSiteActivationConfirmPending,
  } = useStudyPlan();
  const isRemodel = isRemodelStudyPlanType(studyPlan?.studyPlanTypeDetail);

  const [showWarning, setShowWarning] = useState(false);
  const [originalValue, setOriginalValue] = useState(null);
  const [tempBoundValue, setTempBoundValue] = useState(sites_to_activate[boundProperty]);
  const [pendingSave, setPendingSave] = useState(false);

  const isEmpty = (value) => value === "" || value === null || value === undefined;

  // Sync tempBoundValue when the actual value changes (e.g., when switching countries)
  useEffect(() => {
    setTempBoundValue(sites_to_activate[boundProperty]);
  }, [sites_to_activate, boundProperty]);

  // Save after the confirmed site count is in the changelog. Calling savePlan in the
  // confirm click would still see the previous plan.
  useEffect(() => {
    if (!pendingSave) return;
    setPendingSave(false);
    savePlan({ isNew: false }).catch((error) => {
      console.error("Error saving plan:", error);
    });
  }, [pendingSave, savePlan]);

  const handleBlur = (value) => {
    const numericValue = value !== "" && value !== null && value !== undefined ? Number(value) : null;
    const requiresConfirmation = isRemodel || !isEditingAPTableEnabled;

    if (numericValue !== originalValue) {
      if (requiresConfirmation) {
        setSiteActivationConfirmPending(true);
        setShowWarning(true);
      } else {
        const currentValue = tempBoundValue !== null ? tempBoundValue : sites_to_activate[boundProperty];
        handleUpdateSiteGroupParameters(sites_to_activate, boundProperty, currentValue);
      }
    } else if (isEmpty(value)) {
      setTempBoundValue(originalValue);
    }
  };

  const handleFocus = () => {
    setOriginalValue(sites_to_activate[boundProperty]);
  };

  const handleWarningConfirm = () => {
    const currentValue = tempBoundValue !== null ? tempBoundValue : sites_to_activate[boundProperty];
    handleUpdateSiteGroupParameters(sites_to_activate, boundProperty, currentValue);

    if (isRemodel) {
      const stagedProfiles = stageRemodelActivationProfiles(
        activationProfiles,
        currentValue,
        originalValue,
      );
      if (stagedProfiles) {
        stageCountryActivationProfiles(countryId, stagedProfiles);
      }
    }

    setShowWarning(false);
    setOriginalValue(null);
    setSiteActivationConfirmPending(false);
    setPendingSave(true);
  };

  const handleWarningCancel = () => {
    setTempBoundValue(originalValue);
    setShowWarning(false);
    setOriginalValue(null);
    setSiteActivationConfirmPending(false);
  };

  return {
    showWarning,
    tempBoundValue,
    setTempBoundValue,
    handleBlur,
    handleFocus,
    handleWarningConfirm,
    handleWarningCancel,
  };
};
